import { useTheme } from 'next-themes';
import { useTheme as useMuiTheme } from '@mui/material';
import { ClerkProvider } from '@clerk/clerk-react';
import { dark } from '@clerk/themes';
import { type ReactNode } from 'react';
import { deDE, enUS } from '@clerk/localizations';
import { useTranslation } from 'react-i18next';
import type { SupportedLocales } from '@/i18n/i18n.ts';
import { useSearchParams } from 'react-router';

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!PUBLISHABLE_KEY) {
  throw new Error('Add your Clerk Publishable Key to the .env.local file');
}

const localeToClerkTranslationDictionary: Record<string, typeof enUS> = {
  en: enUS,
  de: deDE,
};

export default function AuthenticationProvider({ children }: { children: ReactNode }) {
  const [searchParams] = useSearchParams();
  const queryParams = Array.from(searchParams.entries());
  const redirectUrl =
    queryParams.length > 0
      ? `${window.location.origin}${window.location.pathname}?${new URLSearchParams(queryParams)}`
      : window.location.origin;
  const { theme } = useTheme();
  const muiTheme = useMuiTheme();
  const { i18n } = useTranslation();

  const locale = (i18n.language as SupportedLocales) || 'en';

  const clerkTranslationDictionary = localeToClerkTranslationDictionary[locale] ?? enUS;
  return (
    <ClerkProvider
      appearance={{
        baseTheme: theme === 'dark' ? dark : undefined,
        variables: {
          colorPrimary: muiTheme.palette.primary.main,
        },
      }}
      localization={clerkTranslationDictionary}
      publishableKey={PUBLISHABLE_KEY}
      afterSignOutUrl="/"
      signInForceRedirectUrl={redirectUrl}
      signUpForceRedirectUrl={redirectUrl}
    >
      {children}
    </ClerkProvider>
  );
}
